'use client';

import { JSX, useEffect, useState } from 'react';
import { Typography } from '@/components/atoms/Typography/Typography';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { Video } from '@/components/atoms/Video/Video';
import { Image } from '@/components/atoms/Image/Image';
import { TProjectVisibility } from '@/types/global';
import { capitalizeFirstLetter, capitalizeFirstLettersAllWords, replaceServicesOldWithNew } from '@/utils/generic';
import { useTranslations } from 'next-intl';
import { IProjectFilteredList } from '../FilteredProjectsGallery.types';
import { usePageStore } from '@/store/usePageStore';
import React from 'react';
import * as Styled from './ProjectsList.styled';
import { emailEncoded } from '@/utils/constants';

interface IProjectList {
  projectList: IProjectFilteredList;
  numProjectsToShow: number;
  isPrivatePresentation?: boolean;
  isSpecificWorkPage?: boolean;
  isAdditionalList?: boolean;
  isImagesWithProjects?: boolean;
  className?: string;
  ref?: React.RefObject<HTMLDivElement>;
}

/** 
 * Renders the list of projects on the FilteredProjectsGallery component
 * as a result of the projects api call
 **/
const ProjectsListComponent = React.forwardRef<HTMLDivElement, IProjectList>(
  (
    {
      projectList,
      numProjectsToShow,
      isPrivatePresentation = false,
      isSpecificWorkPage,
      className = '',
      isAdditionalList = false,
      isImagesWithProjects = false,
    },
    ref
  ): JSX.Element => {
    const isUserLogged = usePageStore((s) => s.isUserLogged);
    const locale = usePageStore((s) => s.locale);
    const t = useTranslations('work');
    const content = t.raw('filteredProjectsGallery') as {
      titlePrivateProject: string;
      textPrivateProject: string;
    };
    const refProjectList = React.useRef<HTMLDivElement>(null);
    const [shouldPageScroll, setShouldPageScroll] = useState(true);

    useEffect(() => {
      if (typeof window !== 'undefined') {
        setShouldPageScroll(window.innerWidth <= 1200);
      }
    }, []);

    const isBlockedByPrivacy = (visibility: TProjectVisibility = 'open') => {
      return (visibility === 'private' && !isUserLogged) && !isPrivatePresentation;
    };

    return (
      projectList && (
        <Styled.ProjectList
          ref={refProjectList}
          isLoader={false}
          data-is-list={true}
          className={`${className} ${
            isSpecificWorkPage ? 'specific-work-page-list' : ''} ${
            isAdditionalList ? 'is-additional-list' : 'not-additional-list'} ${
              isPrivatePresentation ? 'is-private-presentation' : ''
            }`}
        >
          {!isAdditionalList && !isPrivatePresentation && (
            <Styled.ListTitleAndLink
              className={projectList.title ? '' : 'empty-title'}
            >
              <Styled.TitleContainer>
                <CustomLink href={`${projectList.viewMoreLink?.slug || '#'}`}>
                  <Typography
                    text={ locale === 'es'
                      ? capitalizeFirstLetter(replaceServicesOldWithNew(projectList?.title || '', locale))
                      : capitalizeFirstLettersAllWords(replaceServicesOldWithNew(projectList?.title || '', locale)) || '&nbsp;'
                    }
                    className="service-title"
                  />
                  <Typography
                    text={projectList.count?.toString()}
                    color={'tertiary'}
                    size="xs"
                  />
                </CustomLink>
              </Styled.TitleContainer>
              <CustomLink
                href={`${projectList.viewMoreLink?.slug || '#'}`}
                text={projectList.viewMoreLink?.text}
                color="secondary"
                hoverColor="primary"
                {...{ scroll: shouldPageScroll }}
              />
            </Styled.ListTitleAndLink>
          )}

          <Styled.ProjectsGrid
            data-grid
            ref={ref}
            className={`${isAdditionalList 
              ? 'is-additional-list' : 'not-additional-list'} ${isImagesWithProjects 
                ? 'images-with-projects-list' : ''
            }`}
          >
            {projectList.projects.map(
              (project, i) => i < numProjectsToShow && project && (
                  <CustomLink
                    key={`project-${i}`}
                    href={
                      isBlockedByPrivacy(project.visibility)
                        ? '#'
                        : `${project.slug}`
                    }
                    isDisabled={isBlockedByPrivacy(project.visibility) || isImagesWithProjects}
                    imageHoverEffect={true}
                    data-is-private={isBlockedByPrivacy(project.visibility)}
                    id={`proj-${project.id}`}
                  >
                    <Styled.Project
                      key={`project-${i}`}
                      isPrivate={isBlockedByPrivacy(project.visibility)}
                    >
                      {project.videoThumbnail ? (
                        <Video
                          {...{
                            ...project.videoThumbnail,
                            vimeoMetadata: project.videoThumbnail?.vimeoMetadata ?? undefined
                          }}
                          includeContainer={true}
                          fill={true}
                          aspectRatio="1.5"
                          includeInSlider={isImagesWithProjects}
                          title={'Video ' + (project.title || '')}
                        />
                      ) : project.image ? (
                        <Image
                          {...project.image}
                          src={project.image?.src || ''}
                          includeContainer={true}
                          aspectRatio="1.5"
                          alt={project.title}
                          fadeInOnLoad={true}
                          className="project-image"
                          sizes = {{
                            mobile: '90vw',
                            tablet: '40vw',
                            desktop: '430px',
                          }}
                          includeInSlider={isImagesWithProjects}
                          lazy={isImagesWithProjects}
                        />
                      ) : isImagesWithProjects && project.video?.vimeoId && (
                        <Image
                          // {...project.image}
                          src={project.video.vimeoMetadata?.poster || ''}
                          includeContainer={true}
                          aspectRatio="1.5"
                          alt={project.video.title || ''} 
                          fadeInOnLoad={true}
                          className="project-image video-image-thumbnail"
                          sizes = {{
                            mobile: '90vw',
                            tablet: '40vw',
                            desktop: '430px',
                          }}
                          includeInSlider={isImagesWithProjects}
                          lazy={isImagesWithProjects}
                          includePlayIcon={true}
                          sliderReference={project.video.vimeoId}
                        />
                      )
                    
                    }
                      {isBlockedByPrivacy(project.visibility) && (
                        <Styled.TextExclusiveContent
                          aspectRatio="4/3"
                          data-is-logged={isUserLogged}
                        >
                          <Typography
                            text={content.titlePrivateProject}
                            color="white"
                            size="base"
                          />
                          <Typography
                            text={{
                              en: `To view the full details of this project,<br> contact us at ${emailEncoded}`,
                              es: `Para ver los detalles completos de este proyecto,<br> contáctenos en ${emailEncoded}`
                            }}
                            color="white"
                            size="sm"
                          />
                        </Styled.TextExclusiveContent>
                      )}
                      <CustomLink
                        isDisabled={!isImagesWithProjects}
                        href={project.slug}
                        data-project-scrollto={`proj-${project.id}`}
                      >
                        <Styled.TextContainer>
                          <Typography text={project.title} />
                          <Typography text={project.location} color="secondary" />
                        </Styled.TextContainer>
                      </CustomLink>
                    </Styled.Project>
                  </CustomLink>
                )
            )}
          </Styled.ProjectsGrid>
        </Styled.ProjectList>
      )
    );
  }
);

// Nota importante: hay que usar memo para evitar que los videos se reinicien al cargar mas listas
export const ProjectsList = React.memo(ProjectsListComponent);
ProjectsListComponent.displayName = 'ProjectsList';
